import { jsPDF } from 'jspdf';
import { Book } from '../database.types';
import { ExportResult } from './types';
import { parseMarkdownBlocks, stripInline, prepareChapters } from './markdown-parser';

function cleanText(text: string): string {
  return text
    .replace(/^Chapter \d+:?\s*[^\n]+\n+/, '')
    .replace(/(?:^|\n)(?:In conclusion|To summarize|Finally)[,\s]*/gi, '')
    .trim();
}

function sanitizeForPdf(text: string): string {
  return text
    .replace(/\u2014/g, '--')
    .replace(/\u2013/g, '-')
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\u2026/g, '...')
    .replace(/\u2022/g, '-')
    .replace(/\u00A9/g, '(c)')
    .replace(/\u00AE/g, '(R)')
    .replace(/\u2122/g, '(TM)')
    .replace(/\u00A0/g, ' ')
    .replace(/\u00B0/g, ' deg')
    .replace(/\u00D7/g, 'x')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
}

// KDP minimum inside (gutter) margins by page count:
//   24-150 pages:  9.6mm  (0.375")
//   151-300 pages: 12.7mm (0.5")
//   301-500 pages: 16.9mm (0.666")
//   501-700 pages: 19.1mm (0.75")
//   701+ pages:    22.2mm (0.875")
// Our default 22mm is generous and covers most books. We only re-render
// if the required gutter exceeds the current value (unlikely but handled).
function getKdpGutterMargin(pageCount: number): number {
  if (pageCount <= 150) return 9.6;
  if (pageCount <= 300) return 12.7;
  if (pageCount <= 500) return 16.9;
  if (pageCount <= 700) return 19.1;
  return 22.2;
}

export async function generateKdpPdf(book: Book): Promise<ExportResult> {
  const pageW = 152.4;
  const pageH = 228.6;
  const pageFormat: [number, number] = [pageW, pageH];
  const outsideMargin = 16;
  const topMargin = 19;
  const bottomMargin = 19;

  // Two-pass approach: first pass with generous 22mm gutter to get page count,
  // then check if KDP requires a larger gutter (unlikely) and re-render if needed.
  function renderKdpPdf(gutterMargin: number): { pdf: jsPDF; pageCount: number } {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [pageW, pageH], compress: true });

  function getMargins(pageNum: number) {
    const isOdd = pageNum % 2 === 1;
    return { left: isOdd ? gutterMargin : outsideMargin, right: isOdd ? outsideMargin : gutterMargin };
  }
  function getContentWidth(pageNum: number) { const m = getMargins(pageNum); return pageW - m.left - m.right; }
  function lm() { return getMargins(currentPage).left; }
  function contentW() { return getContentWidth(currentPage); }
  function pageBreak(needed: number) {
    if (y > pageH - bottomMargin - needed) { pdf.addPage(pageFormat); currentPage++; y = topMargin; }
  }

  const chapters = prepareChapters(book.chapters || []);
  const chapterStartPages: number[] = [];
  let currentPage = 1;
  let y = 0;

  // Title page
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  const titleLines = pdf.splitTextToSize(sanitizeForPdf(book.title), getContentWidth(1));
  y = pageH / 2 - titleLines.length * 10;
  titleLines.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 10; });
  if (book.subtitle) {
    y += 8;
    pdf.setFontSize(16); pdf.setFont('helvetica', 'normal');
    const subLines = pdf.splitTextToSize(sanitizeForPdf(book.subtitle), getContentWidth(1));
    subLines.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 8; });
  }
  if (book.author) {
    y += 16; pdf.setFontSize(14); pdf.setFont('helvetica', 'normal');
    pdf.text(sanitizeForPdf('by ' + book.author), pageW / 2, y, { align: 'center' });
  }

  // Copyright page
  pdf.addPage(pageFormat); currentPage++;
  pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
  y = topMargin + 40;
  pdf.text(sanitizeForPdf('Copyright (c) ' + new Date().getFullYear() + ' ' + (book.author || 'All rights reserved')), lm(), y); y += 8;
  pdf.text('All rights reserved.', lm(), y); y += 8;
  const copyLines = pdf.splitTextToSize('This book or any portion thereof may not be reproduced or used in any manner whatsoever without the express written permission of the publisher except for the use of brief quotations in a book review.', contentW());
  copyLines.forEach((line: string) => { pdf.text(line, lm(), y); y += 5; });

  // Author Bio (optional)
  if (book.author_bio) {
    pdf.addPage(pageFormat); currentPage++;
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    y = topMargin;
    pdf.text('About the Author', pageW / 2, y, { align: 'center' }); y += 12;
    pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
    const bioLines = pdf.splitTextToSize(sanitizeForPdf(book.author_bio), contentW());
    bioLines.forEach((line: string) => { pageBreak(5); pdf.text(line, lm(), y); y += 5; });
  }

  // TOC placeholder
  pdf.addPage(pageFormat); currentPage++;
  const tocPageNum = currentPage;
  const frontMatterPageCount = currentPage;

  // Chapters
  chapters.forEach((chapter) => {
    pdf.addPage(pageFormat); currentPage++;
    chapterStartPages.push(currentPage);
    y = topMargin;
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    const chTitleLines = pdf.splitTextToSize(sanitizeForPdf(chapter.chapter_title), contentW());
    chTitleLines.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 7; });
    y += 8;

    const blocks = parseMarkdownBlocks(cleanText(chapter.generated_text || ''));
    for (const block of blocks) {
      if (block.type === 'heading') {
        y += 6; pdf.setFontSize(12); pdf.setFont('helvetica', 'bold');
        const hl = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.text || '')), contentW());
        for (const l of hl) { pageBreak(6); pdf.text(l, lm(), y); y += 5.5; }
        y += 3; pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
      } else if (block.type === 'paragraph') {
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        const pl = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.text || '')), contentW());
        for (const l of pl) { pageBreak(5); pdf.text(l, lm(), y); y += 5; }
        y += 4;
      } else if (block.type === 'bullet') {
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        for (const item of block.items || []) {
          const il = pdf.splitTextToSize(sanitizeForPdf(stripInline(item)), contentW() - 10);
          for (let li = 0; li < il.length; li++) { pageBreak(5); if (li === 0) pdf.text('-', lm() + 3, y); pdf.text(il[li], lm() + 10, y); y += 5; }
          y += 1;
        }
        y += 3;
      } else if (block.type === 'numbered-list') {
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        for (let ni = 0; ni < (block.items || []).length; ni++) {
          const ns = (ni + 1) + '. ';
          const ind = pdf.getTextWidth(ns) + 2;
          const il = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.items![ni])), contentW() - ind);
          for (let li = 0; li < il.length; li++) { pageBreak(5); if (li === 0) pdf.text(ns, lm(), y); pdf.text(il[li], lm() + ind, y); y += 5; }
          y += 1;
        }
        y += 3;
      }
    }
  });

  // Notes page — collect all chapter references grouped by chapter title
  const chaptersWithRefs = chapters.filter(ch => ch.chapter_references && ch.chapter_references.trim());
  if (chaptersWithRefs.length > 0) {
    pdf.addPage(pageFormat); currentPage++;
    y = topMargin;
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text('Notes', pageW / 2, y, { align: 'center' });
    y += 12;

    for (const chapter of chaptersWithRefs) {
      pageBreak(12);
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold');
      const chSubheading = sanitizeForPdf(chapter.chapter_title);
      const chSubLines = pdf.splitTextToSize(chSubheading, contentW());
      for (const l of chSubLines) { pageBreak(5); pdf.text(l, lm(), y); y += 5; }
      y += 3;

      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
      const refs = chapter.chapter_references!.split('\n').filter((r: string) => r.trim());
      refs.forEach((ref: string, idx: number) => {
        const refText = sanitizeForPdf(`${idx + 1}. ${ref.trim()}`);
        const refLines = pdf.splitTextToSize(refText, contentW() - 5);
        for (const l of refLines) { pageBreak(5); pdf.text(l, lm() + 5, y); y += 4.5; }
        y += 2;
      });
      y += 4;
    }
  }

  // Fill TOC
  pdf.setPage(tocPageNum);
  const mToc = getMargins(tocPageNum);
  const tocCW = getContentWidth(tocPageNum);
  y = topMargin;
  pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
  pdf.text('Table of Contents', pageW / 2, y, { align: 'center' });
  y += 22;
  pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
  chapters.forEach((chapter, idx) => {
    if (y > pageH - bottomMargin - 10) y = topMargin;
    const pn = String(chapterStartPages[idx] - frontMatterPageCount);
    const nw = pdf.getTextWidth(pn);
    const maxTW = tocCW - nw - 20;
    let dt = sanitizeForPdf(chapter.chapter_title);
    if (pdf.getTextWidth(dt) > maxTW) { const tr = pdf.splitTextToSize(dt, maxTW); dt = tr[0] + '...'; }
    pdf.text(dt, mToc.left, y);
    pdf.text(pn, mToc.left + tocCW, y, { align: 'right' });
    const dw = pdf.getTextWidth('.');
    const dx = mToc.left + pdf.getTextWidth(dt) + 2;
    const de = mToc.left + tocCW - nw - 2;
    if (de > dx) pdf.text('.'.repeat(Math.floor((de - dx) / dw)), dx, y);
    y += 8;
  });

  // Page numbers
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    if (i <= 2) continue;
    pdf.setPage(i); pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(String(i - 2), pageW / 2, pageH - 10, { align: 'center' });
  }

  return { pdf, pageCount: totalPages };
  } // end renderKdpPdf

  // First pass: render with default 22mm gutter
  const defaultGutter = 22;
  const firstPass = renderKdpPdf(defaultGutter);
  const requiredGutter = getKdpGutterMargin(firstPass.pageCount);

  // If required gutter exceeds our default, re-render with the larger gutter
  let finalPdf: jsPDF;
  if (requiredGutter > defaultGutter) {
    const secondPass = renderKdpPdf(requiredGutter);
    finalPdf = secondPass.pdf;
  } else {
    finalPdf = firstPass.pdf;
  }

  const blob = finalPdf.output('blob');
  return { filename: book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '_kdp.pdf', blob, mimeType: 'application/pdf' };
}

export async function generateLeadMagnetPdf(book: Book): Promise<ExportResult> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter', compress: true });
  const pageW = pdf.internal.pageSize.width;
  const pageH = pdf.internal.pageSize.height;
  const margin = { top: 25, bottom: 25, left: 25, right: 25 };
  const cw = pageW - margin.left - margin.right;
  let currentPage = 1;
  let y = 0;

  function pb(needed: number) { if (y > pageH - margin.bottom - needed) { pdf.addPage(); currentPage++; y = margin.top; } }

  const chapters = prepareChapters(book.chapters || []);
  const chapterStartPages: number[] = [];

  // Cover
  pdf.setFontSize(28); pdf.setFont('helvetica', 'bold');
  y = pageH / 2 - 30;
  const titleLines = pdf.splitTextToSize(sanitizeForPdf(book.title), cw);
  titleLines.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 12; });
  if (book.subtitle) {
    y += 6; pdf.setFontSize(20); pdf.setFont('helvetica', 'normal');
    const subLines = pdf.splitTextToSize(sanitizeForPdf(book.subtitle), cw);
    subLines.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 9; });
  }
  if (book.author) { y += 16; pdf.setFontSize(16); pdf.text(sanitizeForPdf('by ' + book.author), pageW / 2, y, { align: 'center' }); }

  // Author Bio
  if (book.author_bio) {
    pdf.addPage(); currentPage++;
    pdf.setFontSize(18); pdf.setFont('helvetica', 'bold');
    y = margin.top; pdf.text('About the Author', pageW / 2, y, { align: 'center' }); y += 12;
    pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
    const bioLines = pdf.splitTextToSize(sanitizeForPdf(book.author_bio), cw);
    bioLines.forEach((line: string) => { pb(6); pdf.text(line, margin.left, y); y += 6; });
  }

  // TOC placeholder
  pdf.addPage(); currentPage++;
  const tocPage = currentPage;
  const frontMatterPages = currentPage;

  // Chapters
  chapters.forEach((chapter) => {
    pdf.addPage(); currentPage++;
    chapterStartPages.push(currentPage);
    y = margin.top;
    pdf.setFontSize(20); pdf.setFont('helvetica', 'bold');
    const chTL = pdf.splitTextToSize(sanitizeForPdf(chapter.chapter_title), cw);
    chTL.forEach((line: string) => { pdf.text(line, pageW / 2, y, { align: 'center' }); y += 9; });
    y += 8;

    const blocks = parseMarkdownBlocks(cleanText(chapter.generated_text || ''));
    for (const block of blocks) {
      if (block.type === 'heading') {
        y += 6; pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
        const hl = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.text || '')), cw);
        for (const l of hl) { pb(7); pdf.text(l, margin.left, y); y += 6.5; }
        y += 3; pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
      } else if (block.type === 'paragraph') {
        pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
        const pl = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.text || '')), cw);
        for (const l of pl) { pb(6); pdf.text(l, margin.left, y); y += 6; }
        y += 5;
      } else if (block.type === 'bullet') {
        pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
        for (const item of block.items || []) {
          const il = pdf.splitTextToSize(sanitizeForPdf(stripInline(item)), cw - 10);
          for (let li = 0; li < il.length; li++) { pb(6); if (li === 0) pdf.text('-', margin.left + 3, y); pdf.text(il[li], margin.left + 10, y); y += 6; }
          y += 1;
        }
        y += 4;
      } else if (block.type === 'numbered-list') {
        pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
        for (let ni = 0; ni < (block.items || []).length; ni++) {
          const ns = (ni + 1) + '. ';
          const ind = pdf.getTextWidth(ns) + 2;
          const il = pdf.splitTextToSize(sanitizeForPdf(stripInline(block.items![ni])), cw - ind);
          for (let li = 0; li < il.length; li++) { pb(6); if (li === 0) pdf.text(ns, margin.left, y); pdf.text(il[li], margin.left + ind, y); y += 6; }
          y += 1;
        }
        y += 4;
      }
    }
  });

  // Notes page — collect all chapter references for lead magnet
  const leadChaptersWithRefs = chapters.filter(ch => ch.chapter_references && ch.chapter_references.trim());
  if (leadChaptersWithRefs.length > 0) {
    pdf.addPage(); currentPage++;
    y = margin.top;
    pdf.setFontSize(20); pdf.setFont('helvetica', 'bold');
    pdf.text('Notes', pageW / 2, y, { align: 'center' });
    y += 14;

    for (const chapter of leadChaptersWithRefs) {
      pb(14);
      pdf.setFontSize(13); pdf.setFont('helvetica', 'bold');
      const chSub = sanitizeForPdf(chapter.chapter_title);
      const chSubLines = pdf.splitTextToSize(chSub, cw);
      for (const l of chSubLines) { pb(6); pdf.text(l, margin.left, y); y += 6; }
      y += 3;

      pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
      const refs = chapter.chapter_references!.split('\n').filter((r: string) => r.trim());
      refs.forEach((ref: string, idx: number) => {
        const refText = sanitizeForPdf(`${idx + 1}. ${ref.trim()}`);
        const refLines = pdf.splitTextToSize(refText, cw - 5);
        for (const l of refLines) { pb(6); pdf.text(l, margin.left + 5, y); y += 5.5; }
        y += 2;
      });
      y += 5;
    }
  }

  // Fill TOC
  pdf.setPage(tocPage);
  y = margin.top;
  pdf.setFontSize(22); pdf.setFont('helvetica', 'bold');
  pdf.text('Table of Contents', pageW / 2, y, { align: 'center' });
  y += 22;
  pdf.setFontSize(13); pdf.setFont('helvetica', 'normal');
  chapters.forEach((chapter, idx) => {
    if (y > pageH - margin.bottom - 10) y = margin.top;
    const pn = String(chapterStartPages[idx] - frontMatterPages);
    const nw = pdf.getTextWidth(pn);
    const maxTW = cw - nw - 20;
    let dt = sanitizeForPdf(chapter.chapter_title);
    if (pdf.getTextWidth(dt) > maxTW) { const tr = pdf.splitTextToSize(dt, maxTW); dt = tr[0] + '...'; }
    pdf.text(dt, margin.left, y);
    pdf.text(pn, margin.left + cw, y, { align: 'right' });
    const dw = pdf.getTextWidth('.');
    const dx = margin.left + pdf.getTextWidth(dt) + 2;
    const de = margin.left + cw - nw - 2;
    if (de > dx) pdf.text('.'.repeat(Math.floor((de - dx) / dw)), dx, y);
    y += 9;
  });

  // Page numbers (skip cover)
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    pdf.setPage(i); pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(String(i - 1), pageW / 2, pageH - 10, { align: 'center' });
  }

  const blob = pdf.output('blob');
  return { filename: book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '_lead.pdf', blob, mimeType: 'application/pdf' };
}
